import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import ImageUploadSection from "./ImageUploadSection";
import RichTextEditor from "./RichTextEditor";

interface Article {
  id: string;
  title: string;
  description: string;
  image_url: string;
  content: string;
  keywords?: string;
  status?: string;
}

interface ArticleFormProps {
  onSuccess: () => void;
  editingArticle?: Article | null;
  initialValues?: {
    title: string;
    description: string;
    keywords?: string;
    content: string;
  } | null;
}

const ArticleForm = ({ onSuccess, editingArticle, initialValues }: ArticleFormProps) => {
  console.log("[ArticleForm] Rendering ArticleForm component", { editingArticle, initialValues });
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [content, setContent] = useState("");
  const [keywords, setKeywords] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editingArticle) {
      setTitle(editingArticle.title);
      setDescription(editingArticle.description);
      setImageUrl(editingArticle.image_url);
      setContent(editingArticle.content);
      setKeywords(editingArticle.keywords || "");
    } else if (initialValues) {
      setTitle(initialValues.title);
      setDescription(initialValues.description);
      setContent(initialValues.content);
      setKeywords(initialValues.keywords || "");
    }
  }, [editingArticle, initialValues]);

  const handleSubmit = async (e: React.FormEvent, status: 'published' | 'draft' = 'published') => {
    e.preventDefault();
    console.log(`[ArticleForm] Submitting form with status: ${status}`);
    setIsSubmitting(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      if (editingArticle) {
        const { error } = await supabase
          .from("articles")
          .update({
            title,
            description,
            image_url: imageUrl,
            content,
            keywords,
            status,
            ...(status === 'published' && !editingArticle.status ? { published_at: new Date().toISOString() } : {})
          })
          .eq("id", editingArticle.id);

        if (error) throw error;

        console.log("[ArticleForm] Article updated successfully");
        toast({
          title: "Success",
          description: `Article ${status === 'published' ? 'published' : 'saved as draft'} successfully`,
        });
      } else {
        const { error } = await supabase.from("articles").insert([
          {
            title,
            description,
            image_url: imageUrl,
            content,
            keywords,
            status,
            created_by: user.id,
            ...(status === 'published' ? { published_at: new Date().toISOString() } : {})
          },
        ]);

        if (error) throw error;

        console.log(`[ArticleForm] Article ${status === 'published' ? 'published' : 'saved as draft'} successfully`);
        toast({
          title: "Success",
          description: `Article ${status === 'published' ? 'published' : 'saved as draft'} successfully`,
        });
      }

      if (!editingArticle) {
        setTitle("");
        setDescription("");
        setImageUrl("");
        setContent("");
        setKeywords("");
      }
      
      onSuccess();
    } catch (error) {
      console.error("[ArticleForm] Error saving article:", error);
      toast({
        title: "Error",
        description: "Failed to save article",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageRemove = () => {
    setImageUrl("");
  };

  return (
    <form onSubmit={(e) => handleSubmit(e)} className="mb-12 space-y-6">
      <div>
        <label htmlFor="title" className="block text-sm font-medium mb-2">
          Title
        </label>
        <Input
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </div>
      
      <div>
        <label htmlFor="description" className="block text-sm font-medium mb-2">
          Description
        </label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
        />
      </div>

      <div>
        <label htmlFor="keywords" className="block text-sm font-medium mb-2">
          Keywords (comma-separated)
        </label>
        <Input
          id="keywords"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="e.g., potato farming, seed certification, Scottish agriculture"
        />
        <p className="text-sm text-gray-500 mt-1">
          Add relevant keywords to help with SEO, separated by commas
        </p>
      </div>

      <div>
        <label htmlFor="content" className="block text-sm font-medium mb-2">
          Content
        </label>
        <RichTextEditor
          content={content}
          onChange={setContent}
        />
      </div>
      
      <ImageUploadSection 
        onImageSelected={(url) => setImageUrl(url)}
        currentImageUrl={imageUrl}
        onImageRemove={handleImageRemove}
      />
      
      <div className="flex space-x-4">
        <Button 
          type="button" 
          disabled={isSubmitting || !imageUrl}
          variant="outline"
          onClick={(e) => handleSubmit(e, 'draft')}
        >
          {isSubmitting ? "Saving..." : "Save as Draft"}
        </Button>
        
        <Button 
          type="submit" 
          disabled={isSubmitting || !imageUrl}
        >
          {isSubmitting ? "Publishing..." : editingArticle && editingArticle.status === 'draft' ? "Publish Draft" : editingArticle ? "Update Article" : "Publish Article"}
        </Button>
      </div>
    </form>
  );
};

export default ArticleForm;
